import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';
import { concatMap, take, takeUntil } from 'rxjs/operators';
import { CheckmarkComponent } from "src/app/+shared/components/checkmark.component/checkmark.component";
import { NetaxeptFinalizeResponseCode } from 'src/app/model/enums/netaxept-finalize-response-code';
import { SCAPaymentEvent } from 'src/app/model/enums/sca-payment-event';
import { StorageKeys } from 'src/app/model/enums/storage-keys';
import { NetaxeptPaymentContext } from 'src/app/model/interfaces/netaxept-payment-context';
import { OrderService, PlayerService, StorageService } from 'src/app/services';
import { NetaxeptService } from 'src/app/services/netaxept.service';
import { PaymentService } from 'src/app/services/payment-service';
import { ReceiptService } from 'src/app/services/receipt-service';
import { MembershipState } from '../../model/enums/membership-state';

declare type View = 'accepted' | 'declined' | 'cancelled';

@Component({
    selector: 'app-nets-payment-return-netaxept',
    templateUrl: 'nets-payment-return-netaxept.component.html',
    styleUrls: ['nets-payment-return-netaxept.component.scss']
})
export class NetsPaymentReturnNetaxeptComponent implements OnInit, OnDestroy
{
    @ViewChild('checkmark', { static: false }) 
    public checkmark: CheckmarkComponent;

    private readonly router: Router;
    private readonly activatedRoute: ActivatedRoute;
    private readonly orderService: OrderService;
    private readonly paymentService: PaymentService;
    private readonly netaxeptService: NetaxeptService;
    private readonly storageService: StorageService;
    private readonly receiptService: ReceiptService;
    private pollOrderSubscription = new Subscription();
    private pollFinalizeSubscription = new Subscription();
    private params: ParamMap;
    private context: NetaxeptPaymentContext;
    public view: View;
    public paymentState: 'pending' | 'error' = 'pending';
    private readonly playerService = inject(PlayerService);
    private membershipState: MembershipState;

    public constructor(
        activatedRoute: ActivatedRoute,
        orderService: OrderService,
        paymentService: PaymentService,
        netaxeptService: NetaxeptService,
        router: Router,
        storageService: StorageService,
        receiptService: ReceiptService
    )
    {
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.orderService = orderService;
        this.paymentService = paymentService;
        this.netaxeptService = netaxeptService;
        this.storageService = storageService;
        this.receiptService = receiptService;
    }

  public async ngOnInit(): Promise<void> {
    this.membershipState = await this.playerService.membershipState();

    this.activatedRoute.queryParamMap.subscribe(params => {
      this.params = params;
      const responseCode = params.get('responseCode');
      const encodedContext = params.get('context') || '';
      this.context = JSON.parse(decodeURIComponent(encodedContext)) as NetaxeptPaymentContext;

      switch (responseCode.toLowerCase()) {
        case 'ok':
          this.handleAccepted();
          break;
        default:
          this.handleCancelled();
          break;
      }
    });
  }

    public onErrorOkClick(): void
    {
        this.handleCancelled();
    }

    private handleAccepted(): void
    {
        const transactionId = this.params.get('transactionId');
        if(transactionId == null)
        {
            console.warn(`Unable to complete transaction. Transaction Id not provided.`);
            return;
        }
        
        const orderId = this.context.orderId;
        if(orderId == null)
        {
            console.warn(`No order id registered for transaction: ${transactionId}`);
            return;
        }
        
        const authResponseCode = parseInt(this.params.get('authCode'), 10) as NetaxeptFinalizeResponseCode;
        if(authResponseCode !== NetaxeptFinalizeResponseCode.Ok)
        {
            this.startFinalizePolling(transactionId, orderId);
            return;
        }

        this.onOrderFinalized(orderId);
    }

    private startFinalizePolling(transactionId: string, orderId: number)
    {
        const stopPoll = new Subject<boolean>();
        let pollCount = 0;
        const maxPollCount = 5;

        this.pollFinalizeSubscription = timer(0, 3000).pipe(
            concatMap(() => this.netaxeptService.finalizeTransaction(transactionId)),
            takeUntil(stopPoll),
        ).subscribe(finalizeResponse => {
            
            pollCount++;
            
            if (finalizeResponse.ResponseCodeAuthorization == NetaxeptFinalizeResponseCode.Ok)
            {
                this.onOrderFinalized(orderId);
                stopPoll.next(true);
                return;
            }

            if (pollCount >= maxPollCount)
            {
                stopPoll.next(true);
                this.paymentState = 'error';
            }
        });
    }

    private onOrderFinalized(orderId: number): void
    {
        this.orderService.getOrderById(orderId).pipe(take(1)).subscribe(orderSmt => {

            this.receiptService.queueOrderSmt(orderSmt);
            const orderSmtId = orderSmt.Id;
            this.trackSmtSaleForSoMe();
            this.netaxeptService.paymentCompleted(orderSmt);
            
            setTimeout(() => {
                this.checkmark.setCompleted(true);
                this.paymentService.onSCAPaymentEvent(orderSmtId, SCAPaymentEvent.Success).subscribe(() => {
                    setTimeout(() => {
                        this.router.navigate(['/kvittering']);
                    }, 2000);
                });
            }, 1000);
        });
    }

    private handleCancelled(): void
    {
        this.view = 'cancelled';
        this.storageService.setItem(StorageKeys.NetsPaymentStatus, 'cancelled');
        
        const orderId = this.context.orderId;
        this.paymentService.onSCAPaymentEvent(orderId || -1, SCAPaymentEvent.Cancelled).subscribe(() => {

            let contextUrl = this.context.route;
            const parms = this.parseQueryParams(contextUrl);
            contextUrl = contextUrl.split('?')[0];
            
            this.router.navigate([contextUrl], { queryParams: parms });
        });
    }

    private parseQueryParams(url: string)
    {
        const paramsMap = this.router.parseUrl(url).queryParamMap;
        return paramsMap.keys.reduce((acc, key) => {
            acc[key.toLowerCase()] = paramsMap.get(key)
            return acc;
        }, {});
    }

    private trackSmtSaleForSoMe(): void
    {
        const orderSmt = this.receiptService.getOrders().orderSmt;
        const orderSmtId = orderSmt.Id;

        const contextRoute = this.context.route;
        const relatedSubscriptionOrder  = this.receiptService.getOrders().orderSubscription;

        // ny kunde
        if (contextRoute.includes('/betaling/spil-med-nu') && relatedSubscriptionOrder != null)
        {
            // Set order.id on dk sale to bs order id in order to relate in google analytics
            orderSmt.Id = relatedSubscriptionOrder.Id;

            this.orderService.trackSmtSaleOnOrder(orderSmt, this.membershipState);
        }

        // opsalg
        if (contextRoute.includes('/lodder-til-salg/reservation') && relatedSubscriptionOrder != null)
        {
            // Set order.id on dk sale to bs order id in order to relate in google analytics
            orderSmt.Id = relatedSubscriptionOrder.Id;

            this.orderService.trackSmtSaleOnOrder(orderSmt, this.membershipState);
        }

        // eksisterende (My page shop)
        if (contextRoute.includes('/min-side/?action=openShopFmt') && relatedSubscriptionOrder != null)
        {
            // Set order.id on dk sale to bs order id in order to relate in google analytics
            orderSmt.Id = relatedSubscriptionOrder.Id;
            
            // TODO: Check tracking brand is used in google tracking
            this.orderService.trackSmtSaleOnOrder(orderSmt, this.membershipState);
        }

        // SMT
        if (contextRoute.includes('/min-side/?action=openShopSmt'))
        {
            this.orderService.trackSmtSaleOnOrder(orderSmt, this.membershipState);
        }

        // Set Id back to original to avoid errors.
        orderSmt.Id = orderSmtId;
    }


    public ngOnDestroy(): void
    {
        this.pollFinalizeSubscription.unsubscribe();
        this.pollOrderSubscription.unsubscribe();
    }
}
